import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment/moment';
import { Observable, firstValueFrom } from 'rxjs';
import { FormSubmissionApiService, IFormSubmission, ILocation, ISubmitFormOptions } from 'shared';

import { FileSharePopupComponent } from '../components/modals/file-share-popup/file-share-popup.component';
import { DomService } from './dom.service';

@Injectable({ providedIn: 'root' })
export class FormSubmissionService {
    private readonly formSubmissionApi = inject(FormSubmissionApiService);
    private readonly translateService = inject(TranslateService);
    private readonly domService = inject(DomService);

    public submitForm(
        formId: string,
        result: string,
        location: ILocation,
        options?: ISubmitFormOptions
    ): Promise<IFormSubmission> {
        return firstValueFrom(this.formSubmissionApi.submitForm(formId, result, location, options));
    }

    public async uploadFile(
        files: Array<File>,
        formId: string,
        question: string
    ): Promise<Array<{ id: string; url: string; contentType: string }>> {
        return await firstValueFrom(this.formSubmissionApi.uploadFile(files, formId, question));
    }

    public showFileSharePopup(submission: IFormSubmission): void {
        const formName = submission.form.title[this.translateService.currentLang] ?? submission.form.title.en ?? 'form';
        const shareTitle = this.translateService.instant('history.details.formSubmission.shareTitle', {
            title: formName,
        });
        FileSharePopupComponent.showPopup(
            this.domService,
            () =>
                this.getPDFFile(submission).then((pdf) => {
                    if (!pdf) throw new Error('Form PDF download failed');
                    return pdf;
                }),
            this.getPDFFileName(submission),
            shareTitle,
            'history.details.formSubmission.shareText',
            'history.details.formSubmission.shareTextFallback',
            'PDF'
        );
    }

    public getPDFFileName(submission: IFormSubmission): string {
        const formName =
            submission?.form.title[this.translateService.currentLang] ?? submission?.form.title.en ?? 'form';
        const timestamp = moment.unix(submission?.timestamp ?? 0);
        const submissionDate = timestamp.format('YYYY-MM-DD');
        return `${formName}_${submissionDate}.pdf`;
    }

    public async getPDFFile(submission: IFormSubmission, exportDefinitionId?: string): Promise<File | null> {
        const fileName = this.getPDFFileName(submission);
        try {
            return await firstValueFrom(
                this.formSubmissionApi.getFormSubmissionPdf(submission.id, fileName, exportDefinitionId)
            );
        } catch (e) {
            console.error('Error downloading PDF:', e);
            return null;
        }
    }

    public getFormSubmission(id: string): Observable<IFormSubmission> {
        return this.formSubmissionApi.getFormSubmission(id);
    }
}
